import { api, direRefus } from '../api.ts';
import { h, instant, mot, peindre, type Brut } from '../ui.ts';

interface Compte {
  id: string; email: string; trigramme: string; prenom: string; nom: string;
  role: string; statut: string; totp_actif: boolean; vu_le: string | null; codes_secours: number;
}
interface Parametre { cle: string; valeur: unknown; modifie_le: string }
interface Ligne {
  id: string; le: string; trigramme: string; action: string; entite: string | null;
  detail: unknown;
  /** Résolus à la lecture, jamais écrits au journal : à la purge, ils s'effacent d'eux-mêmes. */
  auteur: string | null; cible: string | null;
}
interface Couts { mois: { total: number; appels: number }; par_type: Record<string, unknown>[] }
interface Echeances {
  a_informer: { id: string; prenom: string; nom: string; email: string | null }[];
  a_purger: { id: string; prenom: string; nom: string; dernier_contact: string | null }[];
  opposes: { id: string; prenom: string; nom: string; opposition_le: string }[];
}

const LIBELLES: Record<string, string> = {
  seuil_telechargements_mois: 'Seuil de sorties de documents par mois et par utilisateur'
    + ' — CV source téléchargés, CV WAKEA produits et dossiers de présentation confondus',
  retention_mois: 'Conservation d’un dossier sans contact, en mois',
  delai_information_jours: 'Délai d’information des personnes, en jours',
};

/**
 * Les échéances RGPD, à l'écran et non dans un message fugace.
 *
 * L'opposition est le cas qui a manqué : la personne quitte le vivier sur-le-champ, son
 * dossier devient invisible partout — vivier, recherche, fiche — et il ne restait au
 * journal qu'une ligne `rgpd.opposition` portant un identifiant qui ne menait nulle part.
 * Impossible de savoir qui s'était opposé, donc impossible d'en tirer la moindre
 * conséquence : prévenir le consultant qui portait la relation, ne pas réenregistrer la
 * personne le mois suivant, ou simplement exécuter la purge en connaissance de cause.
 *
 * La nommer ici ne contrarie pas le règlement : la personne s'est opposée à ce que le
 * cabinet la garde au vivier, pas à ce qu'il sache qui le lui a demandé — et le cabinet
 * doit le savoir pour honorer la demande. L'écran est réservé aux administrateurs, et le
 * nom disparaît de lui-même à la purge, puisqu'il n'est lu que dans le dossier.
 */
const conformite = (e: Echeances): Brut => h`
  <section>
    <div class="filet"><span class="sur">CONFORMITÉ · ÉCHÉANCES</span>
      <span class="pt sourd">réservé aux administrateurs</span></div>

    <div class="tuiles" style="margin-top:14px">
      <div class="tuile ${e.opposes.length ? 'k' : ''}"><b>${e.opposes.length}</b><span>oppositions reçues</span></div>
      <div class="tuile"><b>${e.a_informer.length}</b><span>personnes à informer</span></div>
      <div class="tuile"><b>${e.a_purger.length}</b><span>dossiers à purger</span></div>
    </div>

    ${e.opposes.length ? h`<div class="carte" style="margin-top:14px;border-color:var(--copper)">
      <div class="sur">OPPOSITIONS REÇUES</div>
      <p class="sm sourd" style="margin:6px 0 10px">Ces dossiers ont quitté le vivier au moment de
         l'opposition. L'effacement définitif passe par la purge, qui reste un geste motivé.</p>
      ${e.opposes.map((o) => h`<div class="rang" style="gap:10px;padding:6px 0;border-top:1px solid var(--hair2)">
        <span class="sm pousse" style="color:var(--obsidian)">${o.prenom} ${o.nom}</span>
        <span class="pt sourd">opposition le ${instant(o.opposition_le)}</span></div>`)}
    </div>` : ''}

    ${e.a_informer.length ? h`<p class="sm sourd" style="margin:10px 0 0">À informer :
      ${e.a_informer.map((x) => `${x.prenom} ${x.nom}`).join(' · ')}.</p>` : ''}
  </section>`;

export async function ecranConsole(chrome: (corps: Brut) => Brut): Promise<void> {
  const [comptes, params, journal, couts, integrite, echeances] = await Promise.all([
    api.get<Compte[]>('/api/comptes'),
    api.get<Parametre[]>('/api/parametres'),
    api.get<Ligne[]>('/api/journal'),
    api.get<Couts>('/api/couts'),
    api.get<{ valide: boolean; lignes: number; rupture?: number }>('/api/journal/integrite'),
    api.get<Echeances>('/api/rgpd/echeances'),
  ]);

  peindre('#app', chrome(h`
    <div class="page">
      <div class="entre"><div><h1 class="h1">Console</h1>
        <p class="sous" style="margin:4px 0 0">Comptes, seuils, journal et coûts · réservée aux administrateurs</p></div>
        <span class="sm ${integrite.valide ? 'sourd' : 'err'}">
          ${integrite.valide
            ? `Journal intègre — ${integrite.lignes} lignes scellées`
            : `Rupture de chaîne à la ligne ${integrite.rupture}`}</span></div>

      <div class="tuiles">
        <div class="tuile k"><b>${couts.mois.total.toFixed(2)} €</b><span>dépense IA du mois</span></div>
        <div class="tuile"><b>${couts.mois.appels}</b><span>traitements IA</span></div>
        <div class="tuile"><b>${comptes.filter((c) => c.statut === 'actif').length}</b><span>comptes actifs</span></div>
        <div class="tuile"><b>${integrite.lignes}</b><span>lignes au journal</span></div>
      </div>

      <section>
        <div class="filet"><span class="sur">COMPTES ET RÔLES</span></div>
        <div class="large"><div>
          <div class="th" style="grid-template-columns:60px 1fr 180px 140px 140px 1fr">
            ${['', 'PERSONNE', 'RÔLE', 'ÉTAT', 'SECOND FACTEUR', 'DERNIER ACCÈS'].map((t) => h`<span>${t}</span>`)}</div>
          ${comptes.map((c) => h`<div class="tr" style="grid-template-columns:60px 1fr 180px 140px 140px 1fr">
            <span class="tri">${c.trigramme}</span>
            <div><div style="font-size:12.5px;color:var(--obsidian)">${c.prenom} ${c.nom}</div>
              <div class="pt sourd">${c.email}</div></div>
            <span class="sm">${c.role}</span>
            <span class="sm" style="color:${c.statut === 'actif' ? 'var(--vert)' : 'var(--ambre)'}">${c.statut}</span>
            <div><span class="sm">${c.totp_actif ? `actif · ${c.codes_secours} secours` : 'non appairé'}</span>
              <button class="pt" style="color:var(--copper);display:block" data-act="reinit-2fa" data-id="${c.id}"
                data-nom="${c.prenom} ${c.nom}">Réinitialiser</button></div>
            <span class="sm sourd">${c.vu_le ? instant(c.vu_le) : 'jamais'}</span></div>`)}
        </div></div>
        <button class="bt p" style="margin-top:12px" data-act="inviter">Inviter une personne</button>
      </section>

      <section>
        <div class="filet"><span class="sur">SEUILS ET CONSERVATION</span></div>
        ${params.map((p) => h`<div class="entre" style="align-items:center;margin-top:11px">
          <span class="sm">${LIBELLES[p.cle] ?? p.cle}</span>
          <div class="rang"><input class="champ" style="width:90px;text-align:right" type="number" min="1"
              value="${String(p.valeur)}" id="p-${p.cle}">
            <button class="bt s" data-act="parametre" data-cle="${p.cle}">Enregistrer</button></div></div>`)}
      </section>

      ${couts.par_type.length ? h`<section>
        <div class="filet"><span class="sur">TRAITEMENTS IA DU MOIS</span></div>
        <div class="large"><div>
          <div class="th" style="grid-template-columns:160px 100px 120px 120px 120px 1fr">
            ${['TYPE', 'APPELS', 'COÛT', 'DURÉE', 'ACCEPTÉS', 'ÉCHECS'].map((t) => h`<span>${t}</span>`)}</div>
          ${couts.par_type.map((t) => h`<div class="tr" style="grid-template-columns:160px 100px 120px 120px 120px 1fr">
            ${['type', 'appels', 'cout', 'duree_ms', 'acceptes', 'echecs'].map((c) =>
              h`<span class="sm">${String(t[c] ?? '—')}</span>`)}</div>`)}
        </div></div></section>` : ''}

      <section>
        <div class="filet"><span class="sur">JOURNAL</span>
          <span class="pt sourd">qui a agi, sur qui, et quand · chaque ligne scelle la précédente</span></div>
        <div class="large"><div style="min-width:760px">
          <div class="th" style="grid-template-columns:130px 150px 210px 180px 1fr">
            ${['QUAND', 'QUI', 'ACTION', 'SUR QUI', 'DÉTAIL'].map((t) => h`<span>${t}</span>`)}</div>
          ${journal.map((l) => h`<div class="tr" style="grid-template-columns:130px 150px 210px 180px 1fr">
            <span class="sm sourd">${instant(l.le)}</span>
            <span class="rang" style="gap:6px"><span class="tri">${l.trigramme}</span>
              <span class="pt sourd" style="overflow:hidden;text-overflow:ellipsis">${l.auteur ?? ''}</span></span>
            <span class="sm">${l.action}</span>
            <span class="sm ${l.cible ? '' : 'sourd'}" style="overflow:hidden;text-overflow:ellipsis">${
              l.cible ?? (l.entite ? '— dossier purgé' : '—')}</span>
            <span class="pt sourd" style="overflow:hidden;text-overflow:ellipsis">${JSON.stringify(l.detail)}</span></div>`)}
        </div></div>
      </section>

      ${conformite(echeances)}

      <div class="enc gl"><span class="sur">CE QUI NE SORT PAS DE MANA</span>
        <p>Aucune fonction d'export dans l'interface courante. Deux procédures existent ici, réservées aux
           administrateurs, motivées, notifiées aux autres administrateurs et inscrites au journal :
           le dossier d'une personne, au titre du droit d'accès, et la base entière, au titre de la réversibilité.</p>
        <div class="rang" style="margin-top:8px">
          <button class="bt s" data-act="echeances">Voir les échéances RGPD</button>
          <button class="bt s" data-act="reversibilite">Export de réversibilité</button></div></div>
    </div>`));
}

export async function enregistrerParametre(cle: string, valeur: number): Promise<void> {
  try { await api.patch(`/api/parametres/${cle}`, { valeur }); mot('Paramètre enregistré.'); }
  catch (e) { mot(direRefus(e)); }
}
